import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { SharePanel } from './SharePanel';
import { messageFor } from '../lib/errors';
import { formatBytes, formatDateTime } from '../ui/format';
import { Badge } from '../ui/Page';
import {
  deleteFile,
  downloadUrl,
  updateFile,
  type FileView,
  type FileVisibility,
  type FolderRow,
} from './api';

/**
 * One file, everything a person can change about it, and the preview.
 *
 * The signed URL is fetched here and only here: opening a file is a deliberate act, so that is the
 * moment to mint a credential with a clock on it. The list never holds one (context.md sections
 * 63-64).
 */
export function FileDetails({
  file,
  folders,
  onClose,
}: {
  file: FileView;
  folders: FolderRow[];
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();

  const [name, setName] = useState(file.name);
  const [tags, setTags] = useState(file.tags.join(', '));
  const [visibility, setVisibility] = useState<FileVisibility>(file.visibility);
  const [folderId, setFolderId] = useState(file.folderId ?? '');
  const [preview, setPreview] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(file.name);
    setTags(file.tags.join(', '));
    setVisibility(file.visibility);
    setFolderId(file.folderId ?? '');
    setPreview(null);
    setConfirming(false);
    setError(null);
  }, [file]);

  // Only formats a browser renders without executing them are previewed inline; everything else is
  // a download, which is the same line the API draws on `content-disposition`.
  const previewable = file.mimeType.startsWith('image/') || file.mimeType === 'application/pdf';

  useEffect(() => {
    if (!previewable) return;

    let cancelled = false;

    downloadUrl(file.id)
      .then((ticket) => {
        if (!cancelled) setPreview(ticket.url);
      })
      .catch(() => {
        // A preview that cannot be signed is not an error worth a banner: the file still opens.
      });

    return () => {
      cancelled = true;
    };
  }, [file.id, previewable]);

  const save = useMutation({
    mutationFn: () =>
      updateFile(file.id, {
        name: name.trim(),
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        visibility,
        folderId: folderId === '' ? null : folderId,
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['files'] });
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: () => deleteFile(file.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['files'] });
      void queryClient.invalidateQueries({ queryKey: ['storage-usage'] });
      onClose();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  async function open(attachment: boolean): Promise<void> {
    try {
      const ticket = await downloadUrl(file.id, attachment);

      window.open(ticket.url, '_blank', 'noopener,noreferrer');
      setError(null);
    } catch (caught) {
      setError(messageFor(caught));
    }
  }

  if (sharing) {
    return (
      <SharePanel
        subject={{ kind: 'FILE', id: file.id, name: file.name }}
        onClose={() => setSharing(false)}
      />
    );
  }

  return (
    <aside className="library__details" aria-label={t('files.details')}>
      <header className="library__details-head">
        <h2>{file.name}</h2>
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      {previewable && preview && (
        <div className="library__preview">
          {file.mimeType.startsWith('image/') ? (
            <img src={preview} alt={file.name} />
          ) : (
            <object data={preview} type="application/pdf" aria-label={file.name} />
          )}
        </div>
      )}

      <dl className="library__meta">
        <div>
          <dt>{t('files.size')}</dt>
          <dd>{formatBytes(file.sizeBytes)}</dd>
        </div>
        <div>
          <dt>{t('files.type')}</dt>
          <dd>{file.mimeType}</dd>
        </div>
        <div>
          <dt>{t('common.createdAt')}</dt>
          <dd>{formatDateTime(file.createdAt)}</dd>
        </div>
        <div>
          <dt>{t('files.status')}</dt>
          <dd>
            <Badge tone={file.status === 'READY' ? 'positive' : 'warning'}>
              {t(`files.status.${file.status}`)}
            </Badge>
          </dd>
        </div>
      </dl>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="field">
          <span>{t('files.name')}</span>
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={255} />
        </label>

        <label className="field">
          <span>{t('files.tags')}</span>
          <input
            value={tags}
            placeholder={t('files.tagsPlaceholder')}
            onChange={(event) => setTags(event.target.value)}
          />
        </label>

        <label className="field">
          <span>{t('files.folder')}</span>
          <select
            className="select"
            value={folderId}
            onChange={(event) => setFolderId(event.target.value)}
          >
            <option value="">{t('files.noFolder')}</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>{t('files.visibility')}</span>
          <select
            className="select"
            value={visibility}
            onChange={(event) => setVisibility(event.target.value as FileVisibility)}
          >
            <option value="PRIVATE">{t('files.vis.PRIVATE')}</option>
            <option value="TENANT">{t('files.vis.TENANT')}</option>
            <option value="PUBLIC">{t('files.vis.PUBLIC')}</option>
          </select>
        </label>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="library__details-actions">
          <button type="submit" className="button button--primary" disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => void open(false)}
          >
            {t('files.open')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => void open(true)}
          >
            {t('files.download')}
          </button>

          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setSharing(true)}
          >
            {t('files.share')}
          </button>

          {confirming ? (
            <>
              <button
                type="button"
                className="button button--danger button--compact"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? t('confirm.working') : t('files.deleteConfirm')}
              </button>

              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setConfirming(false)}
              >
                {t('confirm.cancel')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setConfirming(true)}
            >
              {t('files.delete')}
            </button>
          )}
        </div>
      </form>
    </aside>
  );
}
